/**
 * Today's row beside the EOD history — what can honestly be said mid-session.
 *
 * Open interest is published ONCE, after the close. There is no live open interest, and
 * there is no way to derive one from the tape: a trade changes OI by +1 only when both
 * sides open, by −1 only when both close, and by 0 when one opens against the other. OPRA
 * does not say which, so from today's volume alone the true change lies anywhere in
 * [−volume, +volume]. Any single "live OI" number would be a guess wearing the costume of
 * a fact, and on this board a wrong OI silently corrupts vol/OI, premium and the
 * opening-vs-closing read that hangs off them.
 *
 * So this module refuses the point estimate and returns what is actually provable:
 *   - the last PUBLISHED open interest, carried forward with the session it belongs to;
 *   - today's volume as traded;
 *   - the contracts that MUST be new, because you cannot close more than existed;
 *   - the floor and ceiling tomorrow's open interest has to land between.
 *
 * That is strictly more information than a fabricated number, and none of it can mislead.
 */

export type LiveSessionState = 'pending' | 'live';

export interface LiveOiInput {
  /** `open_interest` from the live snapshot. Intraday this IS the prior close's figure. */
  snapshotOi: number | null | undefined;
  /** Contracts traded so far today. */
  snapshotVolume: number | null | undefined;
  /** Open interest from the most recent completed EOD row, and that row's date. */
  priorOi: number | null | undefined;
  priorDate: string | null | undefined;
  /**
   * The session the EOD row's `open_interest` actually REFLECTS — the one before that row.
   *
   * OPRA publishes open interest the morning after the session it describes, so the figure
   * carried on an EOD row is the previous session's. Verified on SPXW 7785C: 461 traded on
   * 08-18 and the 08-19 row carries the resulting 434. Labelling the row's own date would
   * therefore be a full session out, and on that contract it was also 12x too small.
   */
  priorOiAsOf?: string | null;
}

export interface LiveOiReading {
  /** Last PUBLISHED open interest. Never today's — today's does not exist yet. */
  openInterest: number | null;
  /** The session `openInterest` belongs to, so it cannot be misread as live. */
  openInterestAsOf: string | null;
  /** Contracts traded so far today; null before the session has printed anything. */
  volume: number | null;
  /** Contracts that must have been created today. Only provable once volume exceeds OI. */
  minNewContracts: number | null;
  /** The range tomorrow's published OI must fall in, given today's volume. */
  oiFloor: number | null;
  oiCeiling: number | null;
  /** `pending` until the session prints; a pending session has nothing to show. */
  state: LiveSessionState;
}

function finitePositive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function finiteNonNegative(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export function liveOiReading(input: LiveOiInput): LiveOiReading {
  const snapVol = finiteNonNegative(input.snapshotVolume);
  // Zero volume is not "zero traded" -- before the open, nothing has traded YET, and
  // rendering that as 0 reads as a contract nobody wanted rather than a session that has
  // not started. The two are only distinguishable by refusing to show the row at all.
  const traded = snapVol !== null && snapVol > 0 ? snapVol : null;

  // The snapshot's OI is the last published figure; when it is absent, the last completed
  // EOD row holds the same thing. Falling back to 0 -- as this did -- invents a contract
  // with no open interest, which then makes vol/OI infinite and every derived read wrong.
  // The snapshot's open interest was published this morning, so it describes the last
  // completed session. The EOD row's column describes the session before THAT one, so the
  // two sources carry different "as of" dates and must not share a label.
  const fromSnapshot = finitePositive(input.snapshotOi);
  const oi = fromSnapshot ?? finitePositive(input.priorOi);
  let asOf: string | null = null;
  if (oi !== null) {
    asOf = fromSnapshot !== null ? (input.priorDate ?? null) : (input.priorOiAsOf ?? null);
  }

  const state: LiveSessionState = traded === null ? 'pending' : 'live';

  let minNew: number | null = null;
  let floor: number | null = null;
  let ceiling: number | null = null;
  if (oi !== null && traded !== null) {
    // Closing a contract requires one to exist, so volume beyond the open interest cannot
    // be closing volume. This bound holds whatever the mix of opens and closes was.
    minNew = traded > oi ? traded - oi : null;
    floor = Math.max(0, oi - traded);
    ceiling = oi + traded;
  }

  return {
    openInterest: oi,
    openInterestAsOf: asOf,
    volume: traded,
    minNewContracts: minNew,
    oiFloor: floor,
    oiCeiling: ceiling,
    state,
  };
}

/** How the carried figure should be labelled, so it is never read as live. */
export function openInterestLabel(reading: LiveOiReading): string {
  if (reading.openInterest === null) return 'Not Available';
  const n = reading.openInterest.toLocaleString();
  return reading.openInterestAsOf ? `${n} (as of ${reading.openInterestAsOf})` : n;
}

/**
 * The most recent COMPLETED session in a row set.
 *
 * The live panel used to take the last row outright, which is the in-progress session it
 * had itself appended — so it differenced today against today and reported "unchanged"
 * every time, whatever the market did.
 */
export function lastCompletedSession<T extends object>(rows: readonly T[]): T | null {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row && !(row as { is_today?: boolean }).is_today) return row;
  }
  return null;
}
